import React from "react";

import Welcome from "../../../organisms/teacher/TeacherSurveyModals/Welcome";
import Form from "../../../organisms/teacher/TeacherSurveyModals/Form";
import { ISubmitTeacherSurvey, ITeacherSurvey } from "../../../state";
import Confirmation from "../../../organisms/teacher/TeacherSurveyModals/Confirmation";
import Success from "../../../organisms/teacher/TeacherSurveyModals/Success";
import {
  TEACHER_SURVEY_MODAL_PAGES,
  TeacherSurveyModalPage,
} from "../../../constants/teaching_hint";
import Guide from "../../../organisms/teacher/TeacherSurveyModals/Guide";

type FormDataType = {
  birth_date: string | null;
  years_of_teaching: number | null;
  interests: string;
  class_challenges: string;
  desired_student_outcomes: string;
  subjects: string[];
  school_events: { name: string; description: string; month?: number }[];
};

const DEFAULT_FORM_DATA: FormDataType = {
  birth_date: null,
  years_of_teaching: null,
  interests: "",
  class_challenges: "",
  desired_student_outcomes: "",
  subjects: [],
  school_events: [{ name: "", description: "", month: undefined }],
};

const convertDateFormat = (date: string | null): string => {
  if (!date) return "";
  return date.replace(/-/g, "/");
};

type Props = {
  onClose: () => void;
  onUpdateTeacherSurvey: (teacherSurvey: ISubmitTeacherSurvey) => void;
  calling: boolean;
  teacherSurvey: ITeacherSurvey | null;
  pathname: string;
  onCreateTeacherSurvey: (teacherSurvey: ISubmitTeacherSurvey) => void;
  setShouldConfirmOnClose?: (shouldConfirm: boolean) => void;
};

const ChatBubbleTeacherSurvey: React.FC<Props> = ({
  onClose,
  onUpdateTeacherSurvey,
  calling,
  teacherSurvey,
  pathname,
  onCreateTeacherSurvey,
  setShouldConfirmOnClose,
}) => {
  const [modal, setModal] = React.useState<TeacherSurveyModalPage>(
    TEACHER_SURVEY_MODAL_PAGES.WELCOME,
  );
  const [formData, setFormData] = React.useState<FormDataType>(
    DEFAULT_FORM_DATA,
  );

  React.useEffect(() => {
    if (pathname.includes("guideline")) {
      setModal(TEACHER_SURVEY_MODAL_PAGES.FORM);
    }
  }, [pathname]);

  React.useEffect(() => {
    const shouldConfirm =
      modal === TEACHER_SURVEY_MODAL_PAGES.FORM ||
      modal === TEACHER_SURVEY_MODAL_PAGES.CONFIRMATION;
    setShouldConfirmOnClose?.(shouldConfirm);
  }, [modal, setShouldConfirmOnClose]);

  React.useEffect(() => {
    if (teacherSurvey) {
      setFormData({
        birth_date: convertDateFormat(teacherSurvey.birth_date),
        years_of_teaching: teacherSurvey.years_of_teaching || null,
        interests: teacherSurvey.interests || "",
        class_challenges: teacherSurvey.class_challenges || "",
        desired_student_outcomes: teacherSurvey.desired_student_outcomes || "",
        subjects: teacherSurvey.subjects || [],
        school_events: teacherSurvey.school_events || [
          { name: "", description: "", month: undefined },
        ],
      });
    }
  }, [teacherSurvey]);

  const renderContent = (): React.ReactElement => {
    switch (modal) {
      case TEACHER_SURVEY_MODAL_PAGES.WELCOME:
        return (
          <Welcome
            onClose={onClose}
            onNext={() => setModal(TEACHER_SURVEY_MODAL_PAGES.FORM)}
            onTooltip={() => setModal(TEACHER_SURVEY_MODAL_PAGES.PICO)}
            teacherSurvey={teacherSurvey}
          />
        );
      case TEACHER_SURVEY_MODAL_PAGES.FORM:
        return (
          <Form
            onClose={onClose}
            setModal={setModal}
            setFormData={setFormData}
            formData={formData}
          />
        );
      case TEACHER_SURVEY_MODAL_PAGES.CONFIRMATION:
        return (
          <Confirmation
            setModal={setModal}
            formData={formData}
            onClose={onClose}
            calling={calling}
            onUpdateTeacherSurvey={onUpdateTeacherSurvey}
            onCreateTeacherSurvey={onCreateTeacherSurvey}
            teacherSurvey={teacherSurvey}
          />
        );
      case TEACHER_SURVEY_MODAL_PAGES.SUCCESS:
        return <Success onClose={onClose} />;

      case TEACHER_SURVEY_MODAL_PAGES.PICO:
        return (
          <Guide onBack={() => setModal(TEACHER_SURVEY_MODAL_PAGES.WELCOME)} />
        );
    }
  };

  return <>{renderContent()}</>;
};

export default ChatBubbleTeacherSurvey;
